import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/api";

const SSEContext = createContext(null);

const BACKOFF_SCHEDULE = [3000, 10000, 30000]; // 3s, 10s, 30s

export function SSEProvider({ children }) {
  const { user, token } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState("disconnected"); // connected | connecting | disconnected
  const eventSourceRef = useRef(null);
  const handlersRef = useRef({}); // { event_type: Set<handler> }
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const visibleRef = useRef(!document.hidden);

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!user || !token) return;
    if (eventSourceRef.current) return;
    if (!visibleRef.current) return;

    setConnectionStatus("connecting");

    // Build SSE URL with token as query param (EventSource doesn't support headers)
    const baseUrl = API.replace("/api", "");
    const url = `${baseUrl}/api/sse/stream?token=${encodeURIComponent(token)}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      setConnectionStatus("connected");
      retryCountRef.current = 0;
    };

    // Listen for named events
    const eventTypes = [
      "truck_status_changed",
      "vendor_session_changed",
      "nearby_trucks_count_changed",
      "punch_pending_changed",
    ];

    eventTypes.forEach((eventType) => {
      es.addEventListener(eventType, (e) => {
        try {
          const payload = JSON.parse(e.data);
          const handlers = handlersRef.current[eventType];
          if (handlers) {
            handlers.forEach((handler) => {
              try { handler(payload); } catch (err) { console.error("SSE handler error:", err); }
            });
          }
        } catch (err) {
          console.error("SSE parse error:", err);
        }
      });
    });

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      setConnectionStatus("disconnected");

      // Reconnect with exponential backoff
      if (visibleRef.current && user && token) {
        const delay = BACKOFF_SCHEDULE[Math.min(retryCountRef.current, BACKOFF_SCHEDULE.length - 1)];
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          connect();
        }, delay);
      }
    };
  }, [user, token]);

  // Connect when user is authenticated
  useEffect(() => {
    if (user && token) {
      connect();
    } else {
      cleanup();
      setConnectionStatus("disconnected");
    }
    return cleanup;
  }, [user, token, connect, cleanup]);

  // Page Visibility API — pause when hidden, resume when visible
  useEffect(() => {
    const handleVisibility = () => {
      visibleRef.current = !document.hidden;
      if (document.hidden) {
        cleanup();
        setConnectionStatus("disconnected");
      } else if (user && token) {
        connect();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [user, token, connect, cleanup]);

  const subscribe = useCallback((eventType, handler) => {
    if (!handlersRef.current[eventType]) {
      handlersRef.current[eventType] = new Set();
    }
    handlersRef.current[eventType].add(handler);
  }, []);

  const unsubscribe = useCallback((eventType, handler) => {
    if (handlersRef.current[eventType]) {
      handlersRef.current[eventType].delete(handler);
    }
  }, []);

  return (
    <SSEContext.Provider value={{ connectionStatus, subscribe, unsubscribe }}>
      {children}
    </SSEContext.Provider>
  );
}

export function useSSE() {
  const ctx = useContext(SSEContext);
  if (!ctx) {
    // Return a no-op stub when used outside provider (unauthenticated pages)
    return { connectionStatus: "disconnected", subscribe: () => {}, unsubscribe: () => {} };
  }
  return ctx;
}
